import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { TrendingUp, TrendingDown, Package, BarChart3, Truck } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { type DateRange } from 'react-day-picker';
import { CartesianGrid, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import HttpService from '@/shared/services/http.service';
import { API_CONFIG } from '@/shared/constants/api';
import { getDateRange, QuickFilter, toISOFrom, toISOTo } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { carrierStyle } from '@/lib/packageUtils';
import { usePageHeader } from '@/contexts/HeaderContext';

// --- Types ---

interface CarrierStat {
  carrier: string;
  in: number;
  out: number;
  total: number;
  returns: number;
}

interface DailyStat {
  date: string;
  in: number;
  out: number;
  returns: number;
  carriers: CarrierStat[];
}

interface AnalyticsTotals {
  in: number;
  out: number;
  returns: number;
  carriers: CarrierStat[];
}

interface AnalyticsResponse {
  dailyStats: DailyStat[];
  totals: AnalyticsTotals;
  currentlyInStorage: number;
  olderThan7Days: number;
}

interface PieLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
  name?: string;
  value?: number;
  fill?: string;
  actualValue?: number;
  index?: number;
}

const RADIAN = Math.PI / 180;

const formatPct = (n: number): string => {
  const s = n.toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
};

// Custom tooltip for bar chart
const BarTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
}) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-[var(--shadow-elevated)] px-3 py-2.5 text-sm min-w-[150px]">
      <p className="text-foreground text-xs font-medium mb-2">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center justify-between gap-6 py-0.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: p.color }} />
            <span className="text-foreground/70 text-xs">{p.name}</span>
          </div>
          <span className="font-semibold text-foreground tabular-nums">
            {p.value} <span className="font-normal text-foreground ml-0.5">{t('packages')}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

// --- Sub-components ---

interface KPICardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  colorClass: string;
  bgClass: string;
}

const KPICard = ({ title, value, icon, colorClass, bgClass }: KPICardProps) => (
  <div className="bg-card h-full rounded-md p-2 sm:p-2.5 shadow-[var(--shadow-card)] border border-border/50">
    <div className="flex flex-col h-full justify-between">
      <div className="min-h-[20px] sm:min-h-[28px]">
        <p className="text-xs font-medium text-foreground tracking-wide uppercase leading-tight break-words">{title}</p>
      </div>
      <div className="flex items-end justify-between gap-2 mt-1">
        <p className={`text-xl sm:text-3xl font-bold calculator-font leading-none truncate ${colorClass}`}>{value}</p>
        <div
          className={`w-7 h-7 rounded-sm sm:rounded-md ${bgClass} flex items-center justify-center shrink-0 [&_svg]:w-4 [&_svg]:h-4 sm:[&_svg]:w-5 sm:[&_svg]:h-5`}
        >
          {icon}
        </div>
      </div>
    </div>
  </div>
);

const KPISkeleton = () => (
  <div className="bg-card rounded-md p-1.5 sm:p-4 shadow-[var(--shadow-card)] border border-border/50 animate-pulse">
    <div className="flex items-end justify-between gap-0.5 sm:gap-3">
      <div className="flex-1">
        <div className="h-2 sm:h-3 bg-muted rounded w-3/4 mb-2 sm:mb-3" />
        <div className="h-5 sm:h-9 bg-muted rounded w-2/5" />
      </div>
      <div className="w-5 h-5 sm:w-7 sm:h-7 bg-muted rounded-sm sm:rounded-md" />
    </div>
  </div>
);

const ChartSkeleton = () => <div className="h-60 bg-muted rounded-xl animate-pulse" />;

const EmptyChart = ({ message }: { message: string }) => (
  <div className="h-60 flex items-center justify-center text-foreground text-sm">{message}</div>
);

// Bar chart legend pill
const LegendPill = ({ color, label }: { color: string; label: string }) => (
  <div className="flex items-center gap-1.5">
    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
    <span className="text-xs text-foreground">{label}</span>
  </div>
);

// --- Main Component ---

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [activeFilter, setActiveFilter] = useState<QuickFilter>('today');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [dragStart, setDragStart] = useState<Date | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchAnalytics = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError(false);
    try {
      const response = await HttpService.get(API_CONFIG.path.analytics, { from, to }, { skipNotify: true });
      setAnalytics(response);
    } catch {
      setError(true);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeFilter !== 'custom') {
      const { from, to } = getDateRange(activeFilter);
      fetchAnalytics(from, to);
    }
  }, [activeFilter, fetchAnalytics]);

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (isDragging) return; // drag path handles its own commit
    if (range?.from && range?.to) {
      const diffTime = Math.abs(range.to.getTime() - range.from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      if (diffDays > 31) {
        const adjustedTo = new Date(range.from);
        adjustedTo.setDate(range.from.getDate() + 30);
        range.to = adjustedTo;
      }
    }
    setDateRange(range);
    if (range?.from && range?.to) {
      fetchAnalytics(toISOFrom(format(range.from, 'yyyy-MM-dd')), toISOTo(format(range.to, 'yyyy-MM-dd')));
      setCalendarOpen(false);
    }
  };

  // Select a whole calendar month (capped at today); the 31-day limit still applies to manual ranges.
  const handleMonthSelect = (month: Date) => {
    const from = startOfMonth(month);
    const end = endOfMonth(month);
    const today = new Date();
    const to = end > today ? today : end;
    if (from > today) return;
    setDateRange({ from, to });
    setActiveFilter('custom');
    fetchAnalytics(toISOFrom(format(from, 'yyyy-MM-dd')), toISOTo(format(to, 'yyyy-MM-dd')));
    setCalendarOpen(false);
  };

  const handleDayMouseDown = (day: Date) => {
    if (day > new Date()) return;
    setIsDragging(true);
    setDragStart(day);
    setDateRange({ from: day, to: day });
  };

  const handleDayMouseEnter = (day: Date) => {
    if (!isDragging || !dragStart || day > new Date()) return;
    let from = dragStart < day ? dragStart : day;
    let to = dragStart < day ? day : dragStart;
    const diffTime = Math.abs(to.getTime() - from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays > 31) {
      if (day > dragStart) {
        const newTo = new Date(dragStart);
        newTo.setDate(dragStart.getDate() + 30);
        to = newTo;
      } else {
        const newFrom = new Date(dragStart);
        newFrom.setDate(dragStart.getDate() - 30);
        from = newFrom;
      }
    }
    setDateRange({ from, to });
  };

  const handleDayMouseUp = (day: Date) => {
    if (!isDragging || !dragStart) return;
    let from = dragStart < day ? dragStart : day;
    let to = dragStart < day ? day : dragStart;
    const diffTime = Math.abs(to.getTime() - from.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    if (diffDays > 31) {
      if (day > dragStart) {
        const newTo = new Date(dragStart);
        newTo.setDate(dragStart.getDate() + 30);
        to = newTo;
      } else {
        const newFrom = new Date(dragStart);
        newFrom.setDate(dragStart.getDate() - 30);
        from = newFrom;
      }
    }
    const finalRange = { from, to };
    setDateRange(finalRange);
    setIsDragging(false);
    setDragStart(null);
    fetchAnalytics(toISOFrom(format(from, 'yyyy-MM-dd')), toISOTo(format(to, 'yyyy-MM-dd')));
    setCalendarOpen(false);
  };

  const totals = analytics?.totals;
  const dailyStats = analytics?.dailyStats ?? [];
  const carriers = totals?.carriers ?? [];
  const currentlyInStorage = analytics?.currentlyInStorage ?? '—';
  const olderThan7Days = analytics?.olderThan7Days ?? '—';

  const barChartData = dailyStats.map((d) => {
    const [, month, day] = d.date.split('-').map(Number);
    const label = new Date(2000, month - 1, day).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
    return {
      date: label,
      inwarded: d.in,
      outwarded: d.out,
      returns: d.returns,
    };
  });

  // Colors: primary blue for inwarded, success green for outwarded, amber for returns (matches KPI cards)
  const COLOR_IN = 'hsl(213 70% 50%)'; // --primary
  const COLOR_OUT = 'hsl(145 60% 42%)'; // --success
  const COLOR_RETURNS = 'hsl(38 92% 50%)'; // --warning

  const STANDARD_CARRIERS = ['DHL', 'DPD', 'GLS', 'Hermes', 'Deutsche Post'];
  const allCarrierNames = Array.from(new Set([...STANDARD_CARRIERS, ...carriers.map((c) => c.carrier)]));

  const carrierTableData = allCarrierNames
    .map((name) => {
      const c = carriers.find((curr) => curr.carrier.toLowerCase() === name.toLowerCase());
      return {
        name,
        inwarded: c?.in ?? 0,
        outwarded: c?.out ?? 0,
        returns: c?.returns ?? 0,
      };
    })
    .sort((a, b) => {
      if (a.name === 'Other') return 1;
      if (b.name === 'Other') return -1;
      return b.inwarded + b.outwarded - (a.inwarded + a.outwarded);
    });

  const formatLocalDate = (date: Date) =>
    date.toLocaleDateString(i18n.language, { day: '2-digit', month: 'short', year: 'numeric' });

  const isoToLocalDate = (iso: string) => {
    const [year, month, day] = iso.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getResolvedDateRangeStr = () => {
    if (activeFilter === 'custom') {
      if (!dateRange?.from) return t('select_date_range', 'Select date range');
      return dateRange.to
        ? `${formatLocalDate(dateRange.from)} - ${formatLocalDate(dateRange.to)}`
        : formatLocalDate(dateRange.from);
    }
    const { from, to } = getDateRange(activeFilter);
    const fromDate = isoToLocalDate(from);
    const toDate = isoToLocalDate(to);

    if (activeFilter === 'today' || activeFilter === 'yesterday') {
      return formatLocalDate(fromDate);
    }
    return `${formatLocalDate(fromDate)} - ${formatLocalDate(toDate)}`;
  };

  const quickFilters: { key: QuickFilter; label: string }[] = [
    { key: 'today', label: t('today') },
    { key: 'yesterday', label: t('yesterday') },
    { key: 'last7days', label: t('last_7_days') },
    { key: 'last2weeks', label: t('last_2_weeks') },
    { key: 'last1month', label: t('last_1_month') },
    { key: 'custom', label: t('custom_range') },
  ];

  const KPI_CARDS = [
    {
      title: t('total_inwarded'),
      value: totals?.in ?? '—',
      colorClass: 'text-primary',
      bgClass: 'bg-primary/10',
      icon: <TrendingUp className="text-primary" />,
    },
    {
      title: t('total_outwarded'),
      value: totals?.out ?? '—',
      colorClass: 'text-success',
      bgClass: 'bg-success/10',
      icon: <TrendingDown className="text-success" />,
    },
    {
      title: t('currently_stored'),
      value: currentlyInStorage,
      colorClass: 'text-warning',
      bgClass: 'bg-warning/10',
      icon: <Package className="text-warning" />,
    },
    {
      title: t('older_than_7_days'),
      value: olderThan7Days,
      colorClass: 'text-destructive',
      bgClass: 'bg-destructive/10',
      icon: <Package className="text-destructive" />,
    },
    {
      title: t('total_returns'),
      value: totals?.returns ?? '—',
      colorClass: 'text-orange-500',
      bgClass: 'bg-orange-500/10',
      icon: <Truck className="text-orange-500" />,
    },
  ];

  const headerTitle = useMemo(() => <>📊 {t('dashboard')}</>, [t]);
  const handleHeaderBack = useCallback(() => navigate(-1), [navigate]);

  usePageHeader({ title: headerTitle, onBack: handleHeaderBack });

  return (
    <div className="min-h-[calc(100vh-73px)] bg-background flex flex-col items-center">
      <div className="relative bg-background px-3 sm:px-4 py-3 w-full space-y-2">
        {/* Date Filters */}
        <div className="bg-card rounded-md p-2 shadow-[var(--shadow-card)] border border-border/50">
          <div className="flex flex-nowrap items-center gap-1 overflow-x-auto overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {quickFilters.map(({ key, label }) => {
              if (key === 'custom') {
                return (
                  <Popover
                    key={key}
                    open={calendarOpen}
                    onOpenChange={(open) => {
                      if (open) setCalendarMonth(dateRange?.from ?? new Date());
                      setCalendarOpen(open);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button
                        onClick={() => setActiveFilter('custom')}
                        className={`shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                          activeFilter === 'custom'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground hover:bg-muted/70'
                        }`}
                      >
                        {dateRange?.from
                          ? dateRange.to
                            ? `${dateRange.from.toLocaleDateString(i18n.language, { day: '2-digit', month: 'short' })} - ${dateRange.to.toLocaleDateString(i18n.language, { day: '2-digit', month: 'short' })}`
                            : dateRange.from.toLocaleDateString(i18n.language, { day: '2-digit', month: 'short' })
                          : t('custom_range')}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0"
                      align="end"
                      side="bottom"
                      avoidCollisions
                      collisionPadding={16}
                    >
                      <div
                        onMouseDown={(e) => {
                          const btn = (e.target as HTMLElement).closest('button[data-day]') as HTMLButtonElement | null;
                          const d = btn?.getAttribute('data-day');
                          if (d) handleDayMouseDown(new Date(d));
                        }}
                        onMouseEnter={(e) => {
                          const btn = (e.target as HTMLElement).closest('button[data-day]') as HTMLButtonElement | null;
                          const d = btn?.getAttribute('data-day');
                          if (d) handleDayMouseEnter(new Date(d));
                        }}
                        onMouseUp={(e) => {
                          const btn = (e.target as HTMLElement).closest('button[data-day]') as HTMLButtonElement | null;
                          const d = btn?.getAttribute('data-day');
                          if (d) handleDayMouseUp(new Date(d));
                        }}
                        onMouseLeave={() => {
                          if (isDragging) {
                            setIsDragging(false);
                            setDragStart(null);
                          }
                        }}
                      >
                        <Calendar
                          mode="range"
                          selected={dateRange}
                          onSelect={handleRangeSelect}
                          numberOfMonths={isMobile ? 1 : 2}
                          disabled={(date) => date > new Date()}
                          initialFocus
                          month={calendarMonth}
                          toMonth={new Date()}
                          onMonthChange={setCalendarMonth}
                          components={{
                            CaptionLabel: ({ displayMonth }) => (
                              <button
                                type="button"
                                onClick={() => handleMonthSelect(displayMonth)}
                                className="text-sm font-medium rounded-md px-2 py-1 hover:bg-accent transition-colors"
                              >
                                {displayMonth.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
                              </button>
                            ),
                          }}
                          max={31}
                        />
                      </div>
                      <div className="p-2 border-t border-border bg-muted/20 text-center text-xs text-foreground">
                        ℹ️ {t('max_30_days_note')}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              }
              return (
                <button
                  key={key}
                  onClick={() => {
                    setActiveFilter(key);
                    setDateRange(undefined);
                    setCalendarOpen(false);
                  }}
                  className={`shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    activeFilter === key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/70'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="mt-2 px-1 text-xs text-foreground font-medium flex items-center gap-1.5 border-t border-border/20 pt-2">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-pulse" />
            {getResolvedDateRangeStr()}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-2xl p-4 mb-6 text-destructive text-sm font-medium">
            {t('analytics_error')}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
          {loading
            ? Array.from({ length: 5 }, (_, i) => <KPISkeleton key={i} />)
            : KPI_CARDS.map((card) => <KPICard key={card.title} {...card} />)}
        </div>

        {/* Charts */}
        <div className="flex flex-col gap-3">
          {/* ── Inwarded vs Outwarded Bar Chart ── */}
          <div className="bg-card rounded-2xl p-4 shadow-[var(--shadow-card)] border border-border/50">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">{t('inwarded_vs_outwarded')}</h2>
              {!loading && dailyStats.length > 0 && (
                <div className="flex items-center gap-3">
                  <LegendPill color={COLOR_IN} label={t('packages_inwarded')} />
                  <LegendPill color={COLOR_OUT} label={t('packages_outwarded')} />
                  <LegendPill color={COLOR_RETURNS} label={t('packages_returned')} />
                </div>
              )}
            </div>

            {loading ? (
              <ChartSkeleton />
            ) : dailyStats.length === 0 ||
              barChartData.every((d) => d.inwarded === 0 && d.outwarded === 0 && d.returns === 0) ? (
              <EmptyChart message={t('no_data_available')} />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                    <CartesianGrid
                      stroke="hsl(214 20% 90%)"
                      vertical={false}
                      strokeDasharray="3 3"
                      strokeOpacity={0.7}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: 'hsl(215 10% 50%)' }}
                      axisLine={false}
                      tickLine={false}
                      dy={4}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: 'hsl(215 10% 50%)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<BarTooltip />} cursor={{ fill: 'hsl(214 20% 90%)' }} />
                    <Bar
                      name={t('packages_inwarded')}
                      dataKey="inwarded"
                      fill={COLOR_IN}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                    <Bar
                      name={t('packages_outwarded')}
                      dataKey="outwarded"
                      fill={COLOR_OUT}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                    <Bar
                      name={t('packages_returned')}
                      dataKey="returns"
                      fill={COLOR_RETURNS}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── Carrier Distribution Pie ── */}
          <div className="bg-card rounded-2xl p-4 shadow-[var(--shadow-card)] border border-border/50">
            <h2 className="text-base font-semibold text-foreground">{t('carrier_distribution')}</h2>

            {loading ? (
              <ChartSkeleton />
            ) : carriers.length === 0 ? (
              <EmptyChart message={t('no_data_available')} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="py-3 px-4 font-semibold text-sm text-muted-foreground whitespace-nowrap">
                        {t('carrier_provider', 'Carrier Provider')}
                      </th>
                      <th className="py-3 px-4 font-semibold text-sm text-muted-foreground text-center whitespace-nowrap">
                        {t('packages_inwarded')}
                      </th>
                      <th className="py-3 px-4 font-semibold text-sm text-muted-foreground text-center whitespace-nowrap">
                        {t('packages_outwarded')}
                      </th>
                      <th className="py-3 px-4 font-semibold text-sm text-muted-foreground text-center whitespace-nowrap">
                        {t('packages_returned')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {carrierTableData.map((row) => (
                      <tr
                        key={row.name}
                        className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <span className={`font-semibold first-letter:uppercase ${carrierStyle(row.name).text}`}>
                            {row.name}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center font-medium text-foreground">{row.inwarded}</td>
                        <td className="py-3 px-4 text-center font-medium text-foreground">{row.outwarded}</td>
                        <td className="py-3 px-4 text-center font-medium text-foreground">{row.returns}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
